import React from 'react';
import LocationDisabledBadge from '../../../shared/components/common/LocationDisabledBadge';
import { formatAvailabilityImpactActiveFilterLabel } from '../../../shared/constants/availabilityImpactFilter';
import { findLocationById, isLocationActive } from '../../../shared/utils/locationDisplay';
import { DEVICE_LIST_STATUS_LABELS } from '../constants/filterConstants';
import { isDeviceFilterKeyActive } from '../utils/filterUtils';

const MAINTENANCE_FILTER_KEYS = new Set(['ppmStatus', 'calibrationStatus', 'complianceStatus']);

function renderPillForKey(key, filters, setFilters, locations) {
  switch (key) {
    case 'departments':
      if (!filters.departments.length) return null;
      return (
        <div key="dept-pill" className="filter-pill">
          Department: {filters.departments.join(', ')}
          <span className="filter-pill-remove" onClick={() => setFilters({ ...filters, departments: [] })}>
            ✕
          </span>
        </div>
      );

    case 'locations':
      if (!filters.locations.length) return null;
      return (
        <div key="loc-pill" className="filter-pill">
          <span className="filter-pill-location-list">
            Location:{' '}
            {filters.locations.map((locId, index) => {
              const location = findLocationById(locations, locId);
              return (
                <span key={locId} className="filter-pill-location-item">
                  {index > 0 ? ', ' : ''}
                  {location ? location.name : locId}
                  {location && !isLocationActive(location) && <LocationDisabledBadge />}
                </span>
              );
            })}
          </span>
          <span className="filter-pill-remove" onClick={() => setFilters({ ...filters, locations: [] })}>
            ✕
          </span>
        </div>
      );

    case 'ppmStatus':
      if (!filters.ppmStatus.length) return null;
      return (
        <div key="ppm-pill" className="filter-pill">
          PPM: {filters.ppmStatus.join(', ')}
          <span className="filter-pill-remove" onClick={() => setFilters({ ...filters, ppmStatus: [] })}>
            ✕
          </span>
        </div>
      );

    case 'calibrationStatus':
      if (!filters.calibrationStatus.length) return null;
      return (
        <div key="cal-pill" className="filter-pill">
          Calibration: {filters.calibrationStatus.join(', ')}
          <span className="filter-pill-remove" onClick={() => setFilters({ ...filters, calibrationStatus: [] })}>
            ✕
          </span>
        </div>
      );

    case 'complianceStatus':
      if (!filters.complianceStatus.length) return null;
      return (
        <div key="comp-pill" className="filter-pill">
          Compliance: {filters.complianceStatus.join(', ')}
          <span className="filter-pill-remove" onClick={() => setFilters({ ...filters, complianceStatus: [] })}>
            ✕
          </span>
        </div>
      );

    case 'coverageStatus':
      if (!filters.coverageStatus.length) return null;
      return (
        <div key="cov-pill" className="filter-pill">
          Coverage: {filters.coverageStatus.join(', ')}
          <span className="filter-pill-remove" onClick={() => setFilters({ ...filters, coverageStatus: [] })}>
            ✕
          </span>
        </div>
      );

    case 'serviceContractTypes':
      if (!filters.serviceContractTypes.length) return null;
      return (
        <div key="svc-pill" className="filter-pill">
          Service Contracts: {filters.serviceContractTypes.join(', ')}
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, serviceContractTypes: [] })}
          >
            ✕
          </span>
        </div>
      );

    case 'criticality':
      if (!filters.criticality?.length) return null;
      return (
        <div key="criticality-pill" className="filter-pill">
          Criticality:{' '}
          {filters.criticality
            .map((value) => value.charAt(0).toUpperCase() + value.slice(1))
            .join(', ')}
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, criticality: [] })}
          >
            ✕
          </span>
        </div>
      );

    case 'deviceStatus':
      if (!filters.deviceStatus?.length) return null;
      return (
        <div key="device-status-pill" className="filter-pill">
          Device Status:{' '}
          {filters.deviceStatus
            .map((status) => DEVICE_LIST_STATUS_LABELS[status] || status)
            .join(', ')}
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, deviceStatus: [] })}
          >
            ✕
          </span>
        </div>
      );

    case 'complianceRequired':
      if (!filters.complianceRequired) return null;
      return (
        <div key="compreq-pill" className="filter-pill">
          Requires Compliance
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, complianceRequired: false })}
          >
            ✕
          </span>
        </div>
      );

    case 'calibrationRequired':
      if (!filters.calibrationRequired) return null;
      return (
        <div key="calreq-pill" className="filter-pill">
          Requires Calibration
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, calibrationRequired: false })}
          >
            ✕
          </span>
        </div>
      );

    case 'productCostRange':
      if (!filters.productCostFrom && !filters.productCostTo) return null;
      return (
        <div key="cost-pill" className="filter-pill">
          Cost: ₹{filters.productCostFrom || '0'} - ₹{filters.productCostTo || '∞'}
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, productCostFrom: null, productCostTo: null })}
          >
            ✕
          </span>
        </div>
      );

    case 'installationDateRange':
      if (!filters.installationDateFrom && !filters.installationDateTo) return null;
      return (
        <div key="inst-pill" className="filter-pill">
          Installation: {filters.installationDateFrom} to {filters.installationDateTo}
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, installationDateFrom: null, installationDateTo: null })}
          >
            ✕
          </span>
        </div>
      );

    case 'warrantyDateRange':
      if (!filters.warrantyDateFrom && !filters.warrantyDateTo) return null;
      return (
        <div key="war-pill" className="filter-pill">
          Warranty Date: {filters.warrantyDateFrom} to {filters.warrantyDateTo}
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, warrantyDateFrom: null, warrantyDateTo: null })}
          >
            ✕
          </span>
        </div>
      );

    case 'contractDateRange':
      if (!filters.contractDateFrom && !filters.contractDateTo) return null;
      return (
        <div key="con-pill" className="filter-pill">
          Contract Date: {filters.contractDateFrom} to {filters.contractDateTo}
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, contractDateFrom: null, contractDateTo: null })}
          >
            ✕
          </span>
        </div>
      );

    case 'hasActiveComplaints':
      if (!filters.hasActiveComplaints) return null;
      return (
        <div key="issue-pill" className="filter-pill">
          Complaints: Active Only
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, hasActiveComplaints: false })}
          >
            ✕
          </span>
        </div>
      );

    case 'hasNoActiveComplaints':
      if (!filters.hasNoActiveComplaints) return null;
      return (
        <div key="no-complaint-pill" className="filter-pill">
          Complaints: None
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, hasNoActiveComplaints: false })}
          >
            ✕
          </span>
        </div>
      );

    case 'impactsAvailability':
      if (!filters.impactsAvailability) return null;
      return (
        <div key="availability-impact-pill" className="filter-pill">
          {formatAvailabilityImpactActiveFilterLabel(filters.impactsAvailability)}
          <span
            className="filter-pill-remove"
            onClick={() => setFilters({ ...filters, impactsAvailability: '' })}
          >
            ✕
          </span>
        </div>
      );

    default:
      return null;
  }
}

/**
 * Flat active filter pills. When 2+ maintenance types are active, wrap them in ( ) with OR inside.
 */
export default function DeviceActiveFilterPills({ filters, setFilters, activeFilterOrder, locations }) {
  const output = [];
  let hasOutput = false;

  const maintenanceKeysActive = activeFilterOrder.filter(
    (key) => MAINTENANCE_FILTER_KEYS.has(key) && isDeviceFilterKeyActive(filters, key)
  );
  const groupMaintenance = maintenanceKeysActive.length >= 2;
  let maintenanceGroupRendered = false;

  const pushAnd = () => {
    if (hasOutput) {
      output.push(
        <span key={`and-${output.length}`} className="filter-and-connector">
          {' '}
          AND{' '}
        </span>
      );
    }
  };

  for (const key of activeFilterOrder) {
    if (!isDeviceFilterKeyActive(filters, key)) {
      continue;
    }

    if (MAINTENANCE_FILTER_KEYS.has(key)) {
      if (groupMaintenance) {
        if (maintenanceGroupRendered) {
          continue;
        }
        maintenanceGroupRendered = true;

        pushAnd();
        output.push(
          <span key="maint-open" className="filter-pill-paren" aria-hidden="true">
            (
          </span>
        );

        maintenanceKeysActive.forEach((maintKey, index) => {
          if (index > 0) {
            output.push(
              <span key={`or-${maintKey}`} className="filter-or-connector">
                {' '}
                OR{' '}
              </span>
            );
          }
          const maintPill = renderPillForKey(maintKey, filters, setFilters, locations);
          if (maintPill) {
            output.push(maintPill);
          }
        });

        output.push(
          <span key="maint-close" className="filter-pill-paren" aria-hidden="true">
            )
          </span>
        );
        hasOutput = true;
        continue;
      }
    }

    const pill = renderPillForKey(key, filters, setFilters, locations);
    if (!pill) {
      continue;
    }

    pushAnd();
    output.push(pill);
    hasOutput = true;
  }

  return output;
}
